import { useState } from "react"
import { z } from "zod"

import {
	Alert,
	Button,
	Checkbox,
	CopyButton,
	Dialog,
	Form,
	FormInput,
	Tooltip,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { WEBHOOK_RESOURCES } from "./shared"

const createWebhookSchema = z.object({
	label: z.string().min(1, "Required"),
	url: z.string().url("Must be a valid URL"),
})

// Default to threads — most common starting point for integrations.
const DEFAULT_EVENTS = new Set<string>(["thread.*"])
const ALL_EVENT_PATTERNS: readonly string[] = WEBHOOK_RESOURCES.map((r) => r.id)

export function CreateWebhookDialog({
	isOpen,
	onClose,
}: {
	isOpen: boolean
	onClose: () => void
}) {
	const ctx = api.useUtils()
	const form = useZodForm({ schema: createWebhookSchema })
	const [selectedEvents, setSelectedEvents] = useState<Set<string>>(
		new Set(DEFAULT_EVENTS),
	)
	const [createdSecret, setCreatedSecret] = useState<{
		url: string
		secret: string
	} | null>(null)

	const createMutation = api.workspace.createWebhook.useMutation({
		onSuccess: async (data) => {
			await ctx.workspace.invalidate()
			setCreatedSecret({ url: data.url, secret: data.secret })
		},
	})

	const close = () => {
		onClose()
		// Defer reset so the success state stays visible during the
		// dialog's exit animation.
		setTimeout(() => {
			form.reset()
			setSelectedEvents(new Set(DEFAULT_EVENTS))
			setCreatedSecret(null)
		}, 200)
	}

	const toggleEvent = (event: string) => {
		setSelectedEvents((prev) => {
			const next = new Set(prev)
			if (next.has(event)) next.delete(event)
			else next.add(event)
			return next
		})
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={close}
			title={createdSecret ? "Webhook created" : "Create webhook"}
			footer={
				createdSecret ? (
					<Button onClick={close}>Done</Button>
				) : (
					<Button
						type="submit"
						form="create-webhook-form"
						isLoading={createMutation.isLoading}
						disabled={selectedEvents.size === 0}
					>
						Create
					</Button>
				)
			}
		>
			{createdSecret ? (
				<Alert header="Save the signing secret" variant="success">
					<div className="text-sm">
						Use it to verify the HMAC-SHA256 signature on incoming events at{" "}
						<span className="font-mono">{createdSecret.url}</span>.
					</div>
					<div className="mt-3 flex w-full flex-row items-center gap-2 truncate">
						<Tooltip label="Copy secret">
							<Button
								variant="link"
								onClick={async () => {
									await navigator.clipboard.writeText(createdSecret.secret)
									toast.success({ title: "Secret copied" })
								}}
								className="break-all font-mono"
							>
								{createdSecret.secret.slice(0, 40) + "..."}
							</Button>
						</Tooltip>
						<CopyButton
							value={createdSecret.secret}
							onCopied={() => toast.success({ title: "Secret copied" })}
						/>
					</div>
				</Alert>
			) : (
				<Form
					id="create-webhook-form"
					form={form}
					onSubmit={(values) => {
						createMutation.mutate({
							label: values.label,
							url: values.url,
							events: Array.from(selectedEvents),
						})
					}}
					className="space-y-3"
				>
					<FormInput name="label" label="Label" placeholder="e.g. Production" />
					<FormInput
						name="url"
						label="URL"
						placeholder="https://example.com/webhooks/productlane"
					/>
					<div>
						<div className="mb-2 mt-4 flex items-end justify-between">
							<div className="text-sm font-medium">Data change events</div>
							<Checkbox
								size="sm"
								// Indeterminate when some-but-not-all resources are
								// picked. Radix takes "indeterminate" as a literal
								// checked value.
								checked={
									selectedEvents.size === 0
										? false
										: selectedEvents.size === ALL_EVENT_PATTERNS.length
											? true
											: "indeterminate"
								}
								onCheckedChange={() => {
									setSelectedEvents(
										selectedEvents.size === ALL_EVENT_PATTERNS.length
											? new Set()
											: new Set(ALL_EVENT_PATTERNS),
									)
								}}
								label="All"
								labelClassName="text-secondary min-w-fit"
								className="gap-1"
								direction="text-first"
							/>
						</div>
						<div className="grid max-h-60 grid-cols-2 gap-x-4 gap-y-2 overflow-y-auto rounded-md p-2">
							{WEBHOOK_RESOURCES.map((r) => (
								<Checkbox
									key={r.id}
									size="sm"
									checked={selectedEvents.has(r.id)}
									onCheckedChange={() => toggleEvent(r.id)}
									label={
										<span className="flex items-center gap-2">
											<span>{r.label}</span>
										</span>
									}
								/>
							))}
						</div>
						{selectedEvents.size === 0 && (
							<div className="mt-1 text-xs text-red-600">
								Select at least one type of event
							</div>
						)}
					</div>
				</Form>
			)}
		</Dialog>
	)
}
